import { Component, Input } from '@angular/core';
import { FormGroup } from '@angular/forms';

import { FinancesConfig, PoDocumentRules } from '../../../../models/makers-finances-mgr.models';
import { DetailMode, SourceType, SOURCE_TYPE, PO_FIELD, PO_SECTION_LABELS, PO_SECTION } from '../../purchase-order.constants';
import { PO_DOC_ERROR_BANNER, PO_DOC_FALLBACKS, PO_DOC_LABELS } from './section-po-document.constants';
import {
  FILES_CARD_ERROR_TYPE,
  FilesCardError,
  FilesCardErrorType,
  FilesCardLabels,
  FilesCardMessages,
  UploadedFileValue,
} from '../../../shared/files-card/files-card.types';

// Purchase order document section: pod-section shell + banner + poLink mode, delegating the
// multi-file dropzone/list to the generic FilesCardComponent. PO documents are LOCAL-ONLY in the
// form: no persistence hooks — the files travel inline in POST/PUT /purchase-orders' `documents`
// array when the form is saved (PUT reconciles by id). The banner is PO-specific chrome, so the
// card's validationError output is mapped here to the CMS banner copy.
@Component({
  selector: 'app-section-po-document',
  templateUrl: './section-po-document.component.html',
  styleUrls: ['./section-po-document.component.scss'],
})
export class SectionPoDocumentComponent {
  @Input() form: FormGroup;
  @Input() mode: DetailMode;
  @Input() sourceType: SourceType;
  // Full FinancesConfig (not just new_purchase_order): the document limits live at the top
  // level (po_document_max_count / po_document_max_size_mb), siblings of new_purchase_order.
  @Input() remoteConfig: FinancesConfig | null;
  @Input() submitted = false;
  // Read-only mode for the PO view: the card hides the dropzone and Remove, shows Download.
  @Input() viewMode = false;
  // External link to the PO document (alternative to uploaded files). When present, the section
  // shows the centered PDF icon + "Go to files" card instead of the files card.
  @Input() poLink: string | null = null;

  readonly sectionLabel = PO_SECTION_LABELS[PO_SECTION.PURCHASE_ORDER_DOCUMENT];
  readonly ERROR_BANNER = PO_DOC_ERROR_BANNER;
  readonly CONTROL_NAME = PO_FIELD.DOCUMENT_FILE;

  readonly cardLabels: FilesCardLabels = {
    dropzone: PO_DOC_LABELS.DROPZONE,
    browse: PO_DOC_LABELS.BROWSE,
    uploading: PO_DOC_LABELS.UPLOADING,
    preview: PO_DOC_LABELS.PREVIEW,
    remove: PO_DOC_LABELS.REMOVE,
    download: PO_DOC_LABELS.DOWNLOAD,
  };

  fileErrorType: FilesCardErrorType = null;
  fileBannerMessage: string | null = null;

  get files(): UploadedFileValue[] {
    return (this.form?.get(PO_FIELD.DOCUMENT_FILE)?.value as UploadedFileValue[] | null) ?? [];
  }

  get isVisible(): boolean {
    if (this.viewMode) {
      return !!this.files.length || this.isLinkMode;
    }
    return this.sourceType === SOURCE_TYPE.CLIENT;
  }

  get isLinkMode(): boolean {
    return this.viewMode && !!this.poLink;
  }

  get isFileRequired(): boolean {
    return this.submitted && !this.files.length;
  }

  get bannerMessage(): string | null {
    if (this.fileErrorType) {
      return this.fileBannerMessage;
    }
    if (this.isFileRequired) {
      return this.poDocuments?.error_messages?.invalid_format?.banner
        ?? PO_DOC_FALLBACKS.FILE_REQUIRED_BANNER;
    }
    return null;
  }

  get acceptExtensions(): string[] {
    return this.poDocuments?.rules?.allowed_extensions
      ?? PO_DOC_FALLBACKS.ALLOWED_EXTENSIONS;
  }

  get acceptMimes(): string[] {
    return this.poDocuments?.rules?.allowed_mime_types
      ?? PO_DOC_FALLBACKS.ALLOWED_MIMES;
  }

  // The nested CMS block the old single-file section read — still the source for formats,
  // messages and the legacy max_size_bytes limit.
  private get poDocuments(): PoDocumentRules | undefined {
    return this.remoteConfig?.new_purchase_order?.purchase_order_documents;
  }

  // Size limit chain: po_document_max_size_mb wins, then the legacy rules.max_size_bytes,
  // then the code default. 0/negative CMS values mean "not configured" for the backend —
  // never pass them through.
  get maxSizeBytes(): number {
    const mb = this.remoteConfig?.po_document_max_size_mb;
    if (mb && mb > 0) {
      return mb * PO_DOC_FALLBACKS.BYTES_PER_MB;
    }
    return this.poDocuments?.rules?.max_size_bytes
      ?? PO_DOC_FALLBACKS.MAX_SIZE_MB * PO_DOC_FALLBACKS.BYTES_PER_MB;
  }

  // Count limit from the CMS (po_document_max_count) with a code fallback. The generic card
  // treats 0 as "no limit", so an unset CMS value must fall back, not pass through.
  get maxFiles(): number {
    const count = this.remoteConfig?.po_document_max_count;
    return count && count > 0 ? count : PO_DOC_FALLBACKS.MAX_FILES;
  }

  get cardMessages(): FilesCardMessages {
    const errMsgs = this.poDocuments?.error_messages;
    return {
      invalidFormat: errMsgs?.invalid_format?.alert ?? PO_DOC_FALLBACKS.INVALID_FORMAT_ALERT,
      sizeExceeded: errMsgs?.size_exceeded?.alert ?? PO_DOC_FALLBACKS.SIZE_EXCEEDED_ALERT,
      maxFilesExceeded: PO_DOC_FALLBACKS.MAX_FILES_EXCEEDED_ALERT(this.maxFiles),
      uploadFailed: errMsgs?.upload_failed?.alert ?? PO_DOC_FALLBACKS.UPLOAD_FAILED_ALERT,
      uploadComplete: this.poDocuments?.success_messages?.upload_complete
        ?? PO_DOC_FALLBACKS.UPLOAD_COMPLETE,
      removed: PO_DOC_FALLBACKS.REMOVED,
      removeFailed: errMsgs?.upload_failed?.alert ?? PO_DOC_FALLBACKS.UPLOAD_FAILED_ALERT,
    };
  }

  onCardValidationError(error: FilesCardError): void {
    this.fileErrorType = error.type;
    this.fileBannerMessage = this.bannerForType(error.type);
  }

  // A successful add/remove clears the sticky error banner — parity with the original section,
  // which reset its error state on every new valid selection and on remove.
  onFilesChanged(_files: UploadedFileValue[]): void {
    this.fileErrorType = null;
    this.fileBannerMessage = null;
  }

  goToLink(): void {
    if (this.poLink) {
      window.open(this.poLink, '_blank');
    }
  }

  private bannerForType(type: FilesCardErrorType): string {
    const errMsgs = this.poDocuments?.error_messages;
    switch (type) {
      case FILES_CARD_ERROR_TYPE.INVALID_FORMAT:
        return errMsgs?.invalid_format?.banner ?? PO_DOC_FALLBACKS.INVALID_FORMAT_BANNER;
      case FILES_CARD_ERROR_TYPE.SIZE_EXCEEDED:
        return errMsgs?.size_exceeded?.banner ?? PO_DOC_FALLBACKS.SIZE_EXCEEDED_BANNER;
      case FILES_CARD_ERROR_TYPE.MAX_FILES_EXCEEDED:
        return PO_DOC_FALLBACKS.MAX_FILES_EXCEEDED_BANNER;
      // UPLOAD_FAILED and any unknown type: generic internal-error banner.
      default:
        return errMsgs?.upload_failed?.banner ?? PO_DOC_FALLBACKS.UPLOAD_FAILED_BANNER;
    }
  }
}
